<script setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { Button, Select } from '@drama/ui'
import { NOVEL_DRAMA_LOCATION_PRESET_LIMIT, isNovelCharacterCardState } from '@drama/contracts/novel'
import FormField from '../NovelFormField.vue'
import NovelIssueNote from '../NovelIssueNote.vue'
import { dramaPresetCounts } from '@drama/novel-authoring'
import { useSectionModel } from './section-model.js'

const props = defineProps({
  modelValue: { type: Object, required: true },
  index: { type: Number, required: true },
  issues: { type: Array, default: () => [] },
  disabled: { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue', 'navigate'])
const { t } = useI18n()
const { invalid, notes, write, error } = useSectionModel(props, emit)
const person = computed(() => props.modelValue.characters[props.index])
const states = computed(() => (person.value.performance_states ?? []).filter(state => state.image))
const config = computed(() => person.value.drama_presets && typeof person.value.drama_presets === 'object' && !Array.isArray(person.value.drama_presets) ? person.value.drama_presets : {})
const rules = computed(() => Array.isArray(config.value.locations) ? config.value.locations : [])
const counts = computed(() => dramaPresetCounts(props.modelValue))
const locations = computed(() => (props.modelValue.locations ?? []).filter(location => location.image))
const available_locations = computed(() => locations.value.filter(location => !rules.value.some(rule => rule.location_id === location.id)))
const local_error = ref('')
const default_image = computed(() => person.value.image || '')
const limit_reached = computed(() => counts.value.location_count >= NOVEL_DRAMA_LOCATION_PRESET_LIMIT)
const path = (...parts) => ['characters', props.index, 'drama_presets', ...parts]

function state_options(selected) {
  const options = states.value.map(state => ({ value: state.id, label: isNovelCharacterCardState(state)
    ? t('novelStudio.fields.cardState') : state.description ? `${state.id} · ${state.description}` : state.id }))
  if (selected && !options.some(item => item.value === selected)) options.push({ value: selected, label: t('novelStudio.dramaPresets.missingSelection', { id: selected }) })
  return options
}

function location_options(index) {
  const selected = rules.value[index]?.location_id
  const options = locations.value.filter(location => location.id === selected || !rules.value.some((rule, rule_index) => rule_index !== index && rule.location_id === location.id))
    .map(location => ({ value: location.id, label: location.name || location.id }))
  if (selected && !options.some(item => item.value === selected)) options.push({ value: selected, label: t('novelStudio.dramaPresets.missingSelection', { id: selected }) })
  return options
}

// Defaults come from the character card. Editing location rules never persists a manual default selection.
function write_rules(locations) { write(path(), { locations }) }

function add_location() {
  if (props.disabled) return
  if (limit_reached.value) {
    local_error.value = 'novelStudio.dramaPresets.limitReached'
    return
  }
  if (!available_locations.value.length || !states.value.length) return
  write_rules([...rules.value, { location_id: available_locations.value[0].id, state_id: states.value[0].id }])
  local_error.value = ''
}

function update_rule(index, key, value) {
  if (props.disabled) return
  if (key === 'location_id' && rules.value.some((rule, rule_index) => rule_index !== index && rule.location_id === value)) return
  write_rules(rules.value.map((rule, rule_index) => rule_index === index ? { ...rule, [key]: value } : rule))
}

function remove_rule(index) {
  if (props.disabled) return
  write_rules(rules.value.filter((_, rule_index) => rule_index !== index))
  local_error.value = ''
}

function state_image(state_id) { return states.value.find(state => state.id === state_id)?.image || '' }
function state_preview_name(state_id) {
  return isNovelCharacterCardState(states.value.find(state => state.id === state_id)) ? t('novelStudio.fields.cardState') : state_id
}
function location_image(location_id) { return locations.value.find(location => location.id === location_id)?.image || '' }
</script>

<template>
  <section
    class="novel-entity__nested novel-drama-presets"
    :data-path="path().join('.')"
    :data-testid="`novel-character-${index}-drama-presets`"
  >
    <NovelIssueNote :notes="notes(path())" />
    <div
      class="novel-drama-presets__default"
      :data-testid="`novel-character-${index}-drama-default`"
    >
      <img
        v-if="default_image"
        :src="default_image"
        :alt="t('novelStudio.dramaPresets.defaultPreview', { name: person.name || person.id })"
      >
      <div class="novel-drama-presets__default-copy">
        <h4>{{ t('novelStudio.dramaPresets.defaultState') }}</h4>
        <p class="novel-entities__hint">
          {{ t('novelStudio.dramaPresets.defaultHelp') }}
        </p>
        <template v-if="!default_image">
          <p
            class="novel-drama-presets__notice"
            :data-testid="`novel-character-${index}-drama-no-card`"
          >
            {{ t('novelStudio.dramaPresets.noCardImage') }}
          </p>
          <Button
            variant="secondary"
            size="sm"
            :data-testid="`novel-character-${index}-drama-edit-card`"
            @click="emit('navigate', 'characters')"
          >
            {{ t('novelStudio.dramaPresets.editCharacters') }}
          </Button>
        </template>
      </div>
    </div>
    <div class="novel-entity__nested-header">
      <h4>{{ t('novelStudio.dramaPresets.locationRules') }}</h4>
      <Button
        variant="secondary"
        size="sm"
        :disabled="disabled || limit_reached || !available_locations.length || !states.length"
        :data-testid="`novel-character-${index}-drama-add-location`"
        @click="add_location"
      >
        {{ t('novelStudio.dramaPresets.addLocation') }}
      </Button>
    </div>
    <p
      v-if="!rules.length"
      class="novel-entities__hint"
    >
      {{ t('novelStudio.dramaPresets.noRules') }}
    </p>
    <p
      v-if="!states.length"
      class="novel-entities__hint"
      :data-testid="`novel-character-${index}-drama-no-state`"
    >
      {{ t('novelStudio.dramaPresets.noStates') }}
    </p>
    <p
      v-if="!locations.length"
      class="novel-entities__hint"
    >
      {{ t('novelStudio.dramaPresets.noLocationImages') }}
    </p>
    <p
      v-else-if="!available_locations.length"
      class="novel-entities__hint"
    >
      {{ t('novelStudio.dramaPresets.allLocationsUsed') }}
    </p>
    <p
      v-if="limit_reached || local_error"
      class="novel-drama-presets__notice"
      role="status"
    >
      {{ t(local_error || 'novelStudio.dramaPresets.limitReached', { limit: NOVEL_DRAMA_LOCATION_PRESET_LIMIT }) }}
    </p>
    <article
      v-for="(rule, rule_index) in rules"
      :key="rule_index"
      class="novel-entity__sub"
      :class="{ 'has-error': invalid(path('locations', rule_index)) }"
      :data-path="path('locations', rule_index).join('.')"
      :data-testid="`novel-character-${index}-drama-location-${rule_index}`"
    >
      <div class="novel-entity__grid">
        <FormField
          :error="error(path('locations', rule_index, 'location_id'))"
          required
          :label="t('novelStudio.dramaPresets.location')"
          :data-path="path('locations', rule_index, 'location_id').join('.')"
        >
          <Select
            :model-value="rule.location_id || ''"
            :options="location_options(rule_index)"
            :placeholder="t('novelStudio.dramaPresets.chooseLocation')"
            :aria-label="t('novelStudio.dramaPresets.location')"
            :disabled="disabled"
            :invalid="invalid(path('locations', rule_index, 'location_id'))"
            block
            @update:model-value="update_rule(rule_index, 'location_id', $event)"
          />
        </FormField>
        <FormField
          :error="error(path('locations', rule_index, 'state_id'))"
          required
          :label="t('novelStudio.dramaPresets.state')"
          :data-path="path('locations', rule_index, 'state_id').join('.')"
        >
          <Select
            :model-value="rule.state_id || ''"
            :options="state_options(rule.state_id)"
            :placeholder="t('novelStudio.dramaPresets.chooseState')"
            :aria-label="t('novelStudio.dramaPresets.state')"
            :disabled="disabled"
            :invalid="invalid(path('locations', rule_index, 'state_id'))"
            block
            @update:model-value="update_rule(rule_index, 'state_id', $event)"
          />
        </FormField>
      </div>
      <div class="novel-drama-presets__previews">
        <img
          v-if="state_image(rule.state_id)"
          :src="state_image(rule.state_id)"
          :alt="t('novelStudio.dramaPresets.statePreview', { id: state_preview_name(rule.state_id) })"
        >
        <img
          v-if="location_image(rule.location_id)"
          :src="location_image(rule.location_id)"
          :alt="t('novelStudio.dramaPresets.locationPreview', { id: rule.location_id })"
        >
      </div>
      <div>
        <Button
          variant="secondary"
          size="sm"
          :disabled="disabled"
          :aria-label="t('novelStudio.dramaPresets.removeLocation', { id: rule.location_id })"
          @click="remove_rule(rule_index)"
        >
          {{ t('novelStudio.fields.remove') }}
        </Button>
      </div>
    </article>
  </section>
</template>

<style scoped>
.novel-drama-presets { color: var(--drama-color-text); }
.novel-drama-presets__default > *, .novel-drama-presets .novel-entity__grid > * { min-width: 0; }
.novel-drama-presets :deep(.drama-select) { min-width: 0; max-width: 100%; }
.novel-drama-presets__notice { margin: 0; color: var(--drama-color-warning); font-size: var(--drama-font-size-sm); line-height: 1.6; }
.novel-drama-presets__default { display: grid; grid-template-columns: calc(var(--drama-space-xl) * 3) minmax(0, 1fr); gap: var(--drama-space-md); align-items: start; }
.novel-drama-presets__default:not(:has(img)) { grid-template-columns: minmax(0, 1fr); }
.novel-drama-presets__default-copy { display: grid; gap: var(--drama-space-sm); justify-items: start; }
.novel-drama-presets__default-copy h4 { margin: 0; font-size: var(--drama-font-size-sm); }
.novel-drama-presets__default img, .novel-drama-presets__previews img { width: 100%; aspect-ratio: 3 / 4; object-fit: contain; border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-sm); background: var(--drama-color-bg); }
.novel-drama-presets__previews { display: grid; grid-template-columns: repeat(2, calc(var(--drama-space-xl) * 3)); gap: var(--drama-space-sm); }
@media (max-width: 600px) { .novel-drama-presets__default { grid-template-columns: minmax(0, 1fr); } .novel-drama-presets__default img { max-width: calc(var(--drama-space-xl) * 3); } }
</style>
